import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, take, takeUntil } from 'rxjs/operators';
import { FieldConfigDTO, LookupEntityConf } from '../../../../../../../system/system-dtos';
import { PageResponseDTO } from '../../../../../../../common/paging-dto';
import { LookupService } from '../../lookup.service';
import { DataType } from '../../../../data-type-component';

const DEFAULT_PAGE_SIZE = 20;

/**
 * Picker modal for FieldType.paged_lookup.
 *
 * Always pages through the server (never falls back to loading the full collection into
 * memory, unlike LookupModalComponent for non-pageable lookups). Typing or pasting a value
 * into the search box also always resolves against the server via LookupService.searchPaged,
 * rather than filtering whatever page happens to be loaded locally.
 */
@Component({
  selector: 'app-lookup-paged-modal',
  templateUrl: './lookup-paged-modal.component.html',
  styleUrls: ['./lookup-paged-modal.component.scss'],
  standalone: false,
})
export class LookupPagedModalComponent implements OnInit, OnDestroy {
  dataSource: MatTableDataSource<any>;

  allFields: FieldConfigDTO[] = [];

  displayedFieldsNames: string[] = [];

  loading = true;

  response: PageResponseDTO<any>;

  lookupConfig: LookupEntityConf;

  searchTerm = '';

  private readonly pageSize = DEFAULT_PAGE_SIZE;

  private readonly searchSubject = new Subject<string>();

  private readonly destroyed = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LookupPagedModalInitialData,
    public dialogRef: MatDialogRef<LookupPagedModalComponent>,
    private lookupService: LookupService
  ) {}

  ngOnInit() {
    this.lookupConfig = this.lookupService.getConfByLookupCode(this.data.lookup);

    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => this.fetchPage(0, this.pageSize, term)),
        takeUntil(this.destroyed)
      )
      .subscribe((resp) => this.applyResponse(resp));

    this.fetchPage(0, this.pageSize, '')
      .pipe(take(1))
      .subscribe((resp) => this.applyResponse(resp));
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  closeDialog() {
    this.dialogRef.close();
  }

  onSearchInput(value: string) {
    this.searchTerm = (value || '').trim();
    this.loading = true;
    this.searchSubject.next(this.searchTerm);
  }

  pageChanged(event: PageEvent) {
    this.loading = true;
    this.fetchPage(event.pageIndex, event.pageSize, this.searchTerm)
      .pipe(take(1))
      .subscribe((resp) => this.applyResponse(resp));
  }

  onRowClick(row: any) {
    this.dialogRef.close(row);
  }

  private fetchPage(offset: number, perPage: number, searchTerm: string) {
    return this.lookupService.searchPaged(this.getUrl(), offset, perPage, searchTerm || undefined, this.data.context);
  }

  private applyResponse(resp: PageResponseDTO<any>) {
    this.response = resp;
    const content = (resp && resp.content) || [];
    this.allFields = this.buildFields(content);
    const fieldNames = this.allFields.map((f) => f.name);
    this.displayedFieldsNames = ((this.lookupConfig && this.lookupConfig.tableFields) || []).filter((f) =>
      fieldNames.includes(f)
    );
    this.dataSource = new MatTableDataSource(content);
    this.loading = false;
  }

  private buildFields(content: any[]): FieldConfigDTO[] {
    const tableFields = (this.lookupConfig && this.lookupConfig.tableFields) || [];
    const keys = Array.from(new Set(content.flatMap((r) => Object.keys(r))));

    return keys
      .filter((k) => tableFields.includes(k))
      .map(
        (k) =>
          ({
            name: k,
            type: this.findFieldType(k),
            label: this.findFieldLabel(k),
          }) as FieldConfigDTO
      );
  }

  private findFieldType(name: string): DataType {
    const fieldType = this.findFieldConfigForName(this.lookupConfig?.fieldTypes, name);
    return fieldType ? DataType[fieldType] : DataType.text;
  }

  private findFieldLabel(name: string): string {
    return this.findFieldConfigForName(this.lookupConfig?.fieldLabels, name) || name;
  }

  private findFieldConfigForName(values: string[], name: string): string {
    const tableFields = this.lookupConfig && this.lookupConfig.tableFields;
    if (!values || values.length === 0 || !tableFields || tableFields.length === 0) {
      return null;
    }
    const idx = tableFields.indexOf(name);
    return idx >= 0 && idx < values.length ? values[idx] : null;
  }

  private getUrl(): string {
    return (
      (this.data.fieldConfig && this.data.fieldConfig.selectUrl) ||
      (this.lookupConfig && this.lookupConfig.defaultSelectUrl)
    );
  }
}

export interface LookupPagedModalInitialData {
  lookup: string;
  fieldConfig: FieldConfigDTO;
  context: any;
}
